import { createAction } from '@reduxjs/toolkit'
import { InteractableObjectManifestJson } from '../API/InteractableObjectJson'
import LabelContainer from '../Containers/LabelContainer/LabelContainer'
import AudioController from '../Controllers/AudioController'
import dispatch from '../Utility/dispatchAction'
import InfoLayer from './InfoLayer'
import InteractiveLayer from './InteractiveLayer/InteractiveLayer'

export const interactableObjectActionType = 'InteractableObject.interact'
export const createInteractableObjectAction = createAction(
    interactableObjectActionType,
    (interactableObject: InteractableObject, action?: string) => ({
        payload: {
            interactableObject,
            action,
        },
    })
)

/**
 * Use this to listen for interaction (right click) with interactable objects
 * @param id Id of InteractableObject
 * @param callback
 * @param once
 * @returns
 */
export const createInteractableObjectListener = (
    id: string,
    callback?: (interactableObject: InteractableObject) => void,
    once = false
): Promise<InteractableObject> =>
    new Promise<InteractableObject>((resolve) => {
        const handler: EventListener = (event) => {
            // @ts-ignore -- WHY: CustomEvent
            const interactableObject = event.detail.interactableObject as InteractableObject

            if (interactableObject.id === id) {
                once && removeEventListener(interactableObjectActionType, handler)
                callback && callback(interactableObject)
                resolve(interactableObject)
            }
        }

        addEventListener(interactableObjectActionType, handler)
    })

export const removeInteractableObjectListener = (listener: () => void) => {
    console.log('removing listener', listener.name)
    removeEventListener(interactableObjectActionType, listener)
}

export default class InteractableObject {
    id: string
    interactiveElement: SVGPolygonElement
    hotSpotElement: SVGCircleElement
    label: string

    inspectData?: {
        audioId?: string
        text: string
    }

    interactData?: {
        audioId?: string
        text: string
        action?: string
    }

    constructor(manifest: InteractableObjectManifestJson, interactiveLayer: InteractiveLayer, infoLayer: InfoLayer) {
        this.id = manifest.id
        this.label = manifest.label

        if (manifest.inspect) {
            this.inspectData = {
                audioId: manifest.inspect.srcURL && AudioController.add(manifest.inspect.srcURL),
                text: manifest.inspect.text,
            }
        }

        if (manifest.interact) {
            this.interactData = {
                audioId: AudioController.add(manifest.interact.srcURL),
                text: manifest.interact.text,
                action: manifest.interact.action,
            }
        }

        // TODO check if elements exist? At least give a warning for game creators
        this.hotSpotElement = infoLayer.element.querySelector(`#${manifest.id + '-info-circle'}`) as SVGCircleElement
        this.interactiveElement = interactiveLayer.element.querySelector(`#${manifest.id}`) as SVGPolygonElement

        if (!this.interactiveElement) {
            throw new Error(
                `Inamove::InteractableObject => could not find corresponding element for "${manifest.id}" in InteractiveLayer SVG!`
            )
        }

        this.interactiveElement.onmouseover = this.handleMouseOver
        this.interactiveElement.onmouseout = this.handleMouseOut
        this.interactiveElement.onclick = this.handleClick
        this.interactiveElement.oncontextmenu = this.handleContextMenu
    }

    commitInteraction = () => {
        dispatch(createInteractableObjectAction(this, this.interactData?.action))
    }

    handleMouseOver = () => {
        LabelContainer.setText(this.label)
    }

    handleMouseOut = () => {
        LabelContainer.clear()
    }

    inspect = async () => {
        if (this.inspectData) {
            const { audioId, text } = this.inspectData

            await AudioController.playActionAudio(audioId, text)
        }
    }

    handleClick = async () => this.inspect()

    interact = async () => {
        if (this.interactData) {
            const { audioId, text } = this.interactData
            await AudioController.playActionAudio(audioId, text)
        }

        this.commitInteraction()
    }

    handleContextMenu = async () => this.interact()

    setInteractionHandler = (action: string, text?: string, audioId?: string) => {
        this.interactData = {
            text: text ?? this.interactData?.text ?? '',
            audioId: audioId ?? this.interactData?.audioId,
            action,
        }
    }

    destroy = () => {
        this.interactiveElement.remove()
        this.hotSpotElement.remove()
        this.inspectData?.audioId && AudioController.remove(this.inspectData.audioId)
        this.interactData?.audioId && AudioController.remove(this.interactData.audioId)
    }

    show = () => {
        this.interactiveElement.classList.remove('hidden')
        this.interactiveElement.classList.remove('hidden')
    }

    hide = () => {
        this.interactiveElement.classList.add('hidden')
        this.hotSpotElement.classList.add('hidden')
    }
}
